import * as React from "react";

import { type NavItem } from "@widgets/layout/Sidebar";

import {
  BarChart2,
  Bot,
  Compass,
  DollarSign,
  FileText,
  LayoutDashboard,
  Mail,
  Megaphone,
  Palette,
  Puzzle,
  ScanLine,
  Settings,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Tag,
  Truck,
  Users,
} from "lucide-react";

const beta = { badge: "BETA", badgeVariant: "warning" } as const;
const comingSoon = {
  badge: "BETA / COMING SOON",
  badgeVariant: "warning",
} as const;

type PlatformNavItem = Omit<NavItem, "children"> & {
  defaultVisible?: boolean;
  children?: PlatformNavItem[];
};

const platformNavItems: PlatformNavItem[] = [
  {
    label: "Dashboard",
    to: "/dashboard",
    icon: <LayoutDashboard className="h-4 w-4" />,
  },
  {
    label: "Getting Started",
    to: "/getting-started",
    icon: <Compass className="h-4 w-4" />,
  },
  {
    label: "Catalog",
    icon: <ShoppingBag className="h-4 w-4" />,
    children: [
      { label: "Products", to: "/catalog/products", perm: "catalog:read" },
      {
        label: "Product Translations",
        to: "/catalog/products/translations",
        perm: "catalog:write",
      },
      {
        label: "Products Import/Export",
        to: "/catalog/products/import-export",
        perm: "catalog:read",
      },
      {
        label: "Barcodes",
        to: "/catalog/barcodes",
        perm: "catalog:read",
        ...beta,
      },
      {
        label: "Reviews",
        to: "/catalog/reviews",
        perm: "catalog:read",
        ...beta,
      },
      {
        label: "Questions",
        to: "/catalog/questions",
        perm: "catalog:read",
        ...beta,
      },
      { label: "Categories", to: "/catalog/categories", perm: "catalog:read" },
      { label: "Brands", to: "/catalog/brands", perm: "catalog:read" },
      { label: "Labels", to: "/catalog/labels", perm: "catalog:read", ...beta },
      { label: "Attributes", to: "/catalog/attributes", perm: "catalog:read" },
    ],
  },
  {
    label: "Orders",
    to: "/orders",
    icon: <ShoppingCart className="h-4 w-4" />,
    perm: "orders:read",
  },
  {
    label: "Point of Sale",
    to: "/pos",
    icon: <ScanLine className="h-4 w-4" />,
    perm: "pos.access",
  },
  {
    label: "Customers",
    icon: <Users className="h-4 w-4" />,
    children: [
      { label: "Customers", to: "/customers", perm: "customers:read" },
      {
        label: "Wishlists",
        to: "/customers/wishlists",
        perm: "customers:read",
        defaultVisible: false,
      },
    ],
  },
  {
    label: "Marketing",
    icon: <Megaphone className="h-4 w-4" />,
    defaultVisible: false,
    children: [
      {
        label: "Newsletter",
        to: "/marketing/newsletter",
        perm: "marketing:read",
        defaultVisible: false,
      },
    ],
  },
  {
    label: "Communication",
    icon: <Mail className="h-4 w-4" />,
    ...beta,
    children: [
      {
        label: "Templates",
        to: "/communication/templates/manage",
        perm: "marketing:read",
      },
      {
        label: "Browser Notifications",
        to: "/communication/browser-notifications",
        perm: "marketing:read",
      },
    ],
  },
  {
    label: "Pricing & Taxes",
    icon: <Tag className="h-4 w-4" />,
    ...beta,
    children: [
      { label: "Coupons", to: "/pricing/coupons", perm: "pricing:read" },
      { label: "Promotions", to: "/pricing/promotions", perm: "pricing:read" },
      { label: "Taxes", to: "/taxes", perm: "taxes:read" },
    ],
  },
  {
    label: "Billing",
    icon: <DollarSign className="h-4 w-4" />,
    children: [
      { label: "My Plan", to: "/billing/plan", perm: "billing:read" },
      {
        label: "Invoices",
        to: "/billing/invoices",
        perm: "billing:read",
        ...comingSoon,
      },
      {
        label: "Subscriptions",
        to: "/billing/subscriptions",
        perm: "billing:read",
        ...comingSoon,
      },
      {
        label: "Usage",
        to: "/billing/usage",
        perm: "billing:read",
        ...comingSoon,
      },
      {
        label: "Reports",
        to: "/billing/reports",
        perm: "billing:read",
        ...comingSoon,
      },
    ],
  },
  {
    label: "Content & CMS",
    icon: <FileText className="h-4 w-4" />,
    defaultVisible: false,
    children: [
      { label: "Pages", to: "/cms/pages", perm: "cms:read" },
      { label: "Blocks", to: "/cms/blocks", perm: "cms:read" },
      { label: "Menus", to: "/cms/menus", perm: "cms:read" },
      { label: "Import/Export", to: "/cms/import-export", perm: "cms:read" },
      { label: "Redirects", to: "/cms/redirects", perm: "cms:read" },
      { label: "Media Library", to: "/cms/media", perm: "cms:read" },
    ],
  },
  {
    label: "Shipping",
    defaultVisible: false,
    icon: <Truck className="h-4 w-4" />,
    children: [
      { label: "Zones", to: "/shipping/zones", perm: "shipping:read" },
      {
        label: "Packaging",
        to: "/shipping/packaging",
        perm: "shipping:read",
      },
    ],
  },
  {
    label: "Integrations",
    icon: <Puzzle className="h-4 w-4" />,
    ...beta,
    children: [
      {
        label: "Sync Apps",
        to: "/integrations",
        perm: "integrations:read",
        ...comingSoon,
      },
      {
        label: "Webhooks",
        to: "/integrations/webhooks",
        perm: "integrations:read",
        ...beta,
      },
      {
        label: "Connectors",
        to: "/integrations/connectors",
        perm: "integrations:read",
        ...beta,
      },
    ],
  },
  {
    label: "Analytics",
    icon: <BarChart2 className="h-4 w-4" />,
    children: [
      {
        label: "Dashboard",
        to: "/analytics/dashboard",
        perm: "analytics:read",
      },
      {
        label: "Customer Analytics",
        to: "/analytics/customers",
        perm: "analytics:read",
      },
      { label: "Traffic", to: "/analytics/traffic", perm: "analytics:read" },
      {
        label: "Conversion",
        to: "/analytics/conversion",
        perm: "analytics:read",
      },
      {
        label: "Activity Log",
        to: "/analytics/activity-log",
        perm: "analytics:read",
        permissionsRequired: ["settings:read"],
      },
      {
        label: "Reports",
        to: "/analytics/reports",
        perm: "analytics:read",
        permissionsRequired: ["customers:read"],
      },
    ],
  },
  {
    label: "Themes",
    icon: <Palette className="h-4 w-4" />,
    defaultVisible: false,
    children: [
      {
        label: "Gallery",
        to: "/themes",
        perm: "themes:read",
        defaultVisible: false,
      },
      {
        label: "Upload",
        to: "/themes/upload",
        perm: "themes:write",
        defaultVisible: false,
      },
    ],
  },
  {
    label: "Security",
    icon: <Shield className="h-4 w-4" />,
    children: [
      { label: "Users", to: "/security/users", perm: "settings:read" },
      { label: "Roles", to: "/security/roles", perm: "settings:read" },
      {
        label: "GDPR",
        to: "/security/gdpr",
        perm: "settings:read",
        ...beta,
      },
    ],
  },
  {
    label: "Settings",
    to: "/settings",
    icon: <Settings className="h-4 w-4" />,
    perm: "settings:read",
  },
  {
    label: "AI Studio",
    to: "/ai",
    perm: "ai:read",
    icon: <Bot className="h-4 w-4" />,
    defaultVisible: true,
  },
];

export type NavigationSettings = { visibility: Record<string, boolean> };
export type NavigationOption = Omit<NavItem, "children"> & {
  defaultVisible: boolean;
  children?: NavigationOption[];
};

function navigationOptions(
  items: PlatformNavItem[],
  parentVisible = true,
): NavigationOption[] {
  return items.map((item) => {
    const defaultVisible =
      parentVisible &&
      (item.defaultVisible ?? (!item.badge && item.label !== "AI Studio"));
    return {
      ...item,
      defaultVisible,
      children: item.children
        ? navigationOptions(item.children, defaultVisible)
        : undefined,
    };
  });
}

export const navigationOptionsList = navigationOptions(platformNavItems);

export function isNavigationItemVisible(
  item: NavigationOption,
  settings?: NavigationSettings | null,
): boolean {
  if (item.to === "/settings") return true;
  if (item.children)
    return item.children.some((child) =>
      isNavigationItemVisible(child, settings),
    );
  const override = item.to ? settings?.visibility?.[item.to] : undefined;
  return typeof override === "boolean" ? override : item.defaultVisible;
}

export function buildNavigation(
  settings?: NavigationSettings | null,
): NavItem[] {
  function resolve(items: NavigationOption[]): NavItem[] {
    return items.flatMap((item) => {
      if (!isNavigationItemVisible(item, settings)) return [];
      const {
        defaultVisible: _defaultVisible,
        badge: _badge,
        badgeVariant: _badgeVariant,
        children,
        ...navItem
      } = item;
      return [
        { ...navItem, ...(children ? { children: resolve(children) } : {}) },
      ];
    });
  }
  return resolve(navigationOptionsList);
}

// Match the most specific known page so a visible parent URL cannot release a hidden child.
export function isNavigationPageReleased(
  pathname: string,
  settings?: NavigationSettings | null,
): boolean {
  const pages = navigationOptionsList.flatMap(
    (item) => item.children ?? [item],
  );
  const page = pages
    .filter(
      (item) =>
        item.to && (pathname === item.to || pathname.startsWith(`${item.to}/`)),
    )
    .sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0))[0];
  return Boolean(page && isNavigationItemVisible(page, settings));
}

export const platformNav = buildNavigation();
export const mvpNav = platformNav;
